import type { HTMLInputAttributes } from "svelte/elements";
import { Locale, type LocalizedString } from "@core/schema/Locale.js";
import { makeInflect } from "@core/utils/makeInflect.js";
import { sameValueForEveryLocale } from "@core/utils/sameValueForEveryLocale.js";

const inflectCharactersCs = makeInflect("znak", "znaky", "znaků");
const inflectCharactersEn = makeInflect("character", "characters", "characters");

const customValidationMessage: Record<NonNullable<HTMLInputAttributes["type"]>, LocalizedString> = {
	email: {
		[Locale.cs]: "Zadejte platnou e-mailovou adresu",
		[Locale.en]: "Enter a valid email address",
	},
	url: {
		[Locale.cs]: "URL adresa (včetně https://)",
		[Locale.en]: "URL address (including https://)",
	},
};

export type ValidationMessageCallback = (input: HTMLInputElement | HTMLSelectElement) => LocalizedString;

function inputOnly(callback: (input: HTMLInputElement) => LocalizedString): ValidationMessageCallback {
	return (input) => {
		if (!(input instanceof HTMLInputElement)) {
			return {
				[Locale.cs]: "Neznámá chyba",
				[Locale.en]: "Unknown error",
			};
		}

		return callback(input);
	};
}

export const customValidationMessages: Record<keyof ValidityState, ValidationMessageCallback> = {
	valid: () => sameValueForEveryLocale(""),
	customError: ({ validationMessage }) => sameValueForEveryLocale(validationMessage),
	valueMissing: () => ({
		[Locale.cs]: "Toto pole je povinné",
		[Locale.en]: "This field is required",
	}),
	typeMismatch: ({ type }) =>
		customValidationMessage[type] ?? {
			[Locale.cs]: "Zadejte platnou hodnotu",
			[Locale.en]: "Enter a valid value",
		},
	patternMismatch: () => ({
		[Locale.cs]: "Zadejte hodnotu ve správném formátu",
		[Locale.en]: "Enter a value in the correct format",
	}),
	badInput: () => ({
		[Locale.cs]: "Zadejte platnou hodnotu",
		[Locale.en]: "Enter a valid value",
	}),
	rangeOverflow: inputOnly((input) => ({
		[Locale.cs]: `Zadejte hodnotu menší nebo rovnou ${input.max}`,
		[Locale.en]: `Enter a value less than or equal to ${input.max}`,
	})),
	rangeUnderflow: inputOnly((input) => ({
		[Locale.cs]: `Zadejte hodnotu větší nebo rovnou ${input.min}`,
		[Locale.en]: `Enter a value greater than or equal to ${input.min}`,
	})),
	stepMismatch: inputOnly((input) => ({
		[Locale.cs]: `Zadejte hodnotu dělitelnou ${input.step}`,
		[Locale.en]: `Enter a value that matches the specified step ${input.step}`,
	})),
	tooLong: inputOnly((input) => ({
		[Locale.cs]: `Zadejte nejvýše ${input.maxLength} ${inflectCharactersCs(input.maxLength)}`,
		[Locale.en]: `Enter no more than ${input.maxLength} ${inflectCharactersEn(input.maxLength)}`,
	})),
	tooShort: inputOnly((input) => ({
		[Locale.cs]: `Zadejte nejméně ${input.minLength} ${inflectCharactersCs(input.minLength)}`,
		[Locale.en]: `Enter at least ${input.minLength} ${inflectCharactersEn(input.minLength)}`,
	})),
};
